import { router, useLocalSearchParams } from "expo-router"
import { View } from "react-native"
import { useChangePasswordMutation } from "@/queries/user"
import { useEffect, useState } from "react"
import FeedbackTextInput from "@/components/input/FeedbackTextInput"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { showErrorToast, showSuccessToast } from "@/components/toasts/AppToast"
import * as signInEvents from "@/analytics/events/sign-in"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import Head from "expo-router/head"
import BFText, { H2 } from "@/components/standard/BFText"
import { A } from "@/components/standard/Html"
import sdk from "@/api/sdk"

// NOTE: For backwards compatibility reasons this path needs to be /reset?token=<token>
// Changing it requires server changes on reset password email
// going out!
export default function ChangePassword() {
  const changePWMutation = useChangePasswordMutation()
  const [password, setPassword] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const { token } = useLocalSearchParams()

  useViewedCallback(() => {
    setStatusBarStyle("light")
    signInEvents.signInViewed()
  })

  useEffect(() => {
    if (!token) {
      showErrorToast({
        title: "No valid token",
        message: "Not a valid reset password link",
      })
      setTimeout(() => {
        router.replace("/register")
      }, 500)
    } else {
      void (async () => {
        try {
          const data = await sdk.ResetPasswordTokenData({
            token: token as string,
          })
          if (data.tokenData?.email) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            setEmail(data.tokenData?.email)
            return
          }
        } catch (e) {
          console.error(e)
        }

        showErrorToast({
          title: "No valid token",
          message: "Not a valid reset password link",
        })
        router.replace("/register")
      })()
    }
  }, [token])

  const onChangePassword = () => {
    changePWMutation.mutate(
      {
        password,
        token: token as string,
      },
      {
        onSuccess: () => {
          showSuccessToast({
            title: "Password changed",
            message: `Your password was changed. Please login.`,
          })

          router.replace("/login")
        },
        onError: (error) => {
          const errorResponse = (error as any)?.response
          if (errorResponse.errors) {
            showErrorToast({
              title: "Reset Email Error",
              message: errorResponse.errors[0].message,
            })
          }
        },
      }
    )
  }

  return (
    <>
      <Head>
        <title>Backflip - Reset Password</title>
      </Head>
      <View className={"flex-1 justify-between"}>
        <View>
          <View className={"flex flex-col"}>
            <View className="pt-10 pb-6 gap-3">
              {email && <H2 className="text-white">{email},</H2>}
              <H2 className="text-white">Reset your password </H2>
            </View>
            <FeedbackTextInput
              placeholder="New Password (min 8 characters)"
              value={password}
              onChange={setPassword}
              isSecured={true}
              returnKeyType="done"
              testID="login-password-text-input"
            />
          </View>

          <BFText className="text-white text-[12px] font-spacegrotesk text-center mb-10">
            Don&apos;t have an account?&nbsp;
            <BFText
              className="underline text-teal font-spacegrotesk"
              onPress={() => {
                router.push("/register")
              }}
            >
              Sign Up
            </BFText>
          </BFText>
        </View>
        <View>
          <PrimaryButton
            testID="reset-password-button"
            title={"Change Password"}
            onPress={onChangePassword}
            disabled={password.length < 8}
          />
          <BFText className="text-white text-[10px] font-spacegrotesk text-center m-[6px]">
            <A
              className="underline text-teal font-spacegrotesk"
              href="https://dobackflip.com/backflip-terms-of-use-agreement/"
            >
              Terms of Use
            </A>
            &nbsp;and&nbsp;
            <A
              className="underline text-teal font-spacegrotesk"
              href="https://dobackflip.com/privacy/"
            >
              Privacy Policy
            </A>
          </BFText>
        </View>
      </View>
    </>
  )
}
